import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, Edit, Trash2, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

const AdminScannedCards = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const { toast } = useToast();
  
  // Check admin access
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  
  useEffect(() => {
    if (user && !isAdmin) {
      toast({ title: "Access Denied", description: "Only admin can access this page", variant: "destructive" });
      navigate('/');
    }
  }, [user, isAdmin, navigate, toast]);

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    try {
      setLoading(true);
      const { scannedContactAPI } = await import('../services/api');
      const allCards = await scannedContactAPI.getAll();
      
      // Sort: processing first, then by date
      const sorted = allCards.sort((a, b) => {
        if (a.isProcessing && !b.isProcessing) return -1;
        if (!a.isProcessing && b.isProcessing) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      
      setCards(sorted);
    } catch (error) {
      console.error('Error loading cards:', error);
      toast({
        title: "Load Failed",
        description: "Failed to load scanned cards.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const reprocessCard = async (cardId, frontImage) => {
    try {
      setProcessing(prev => ({ ...prev, [cardId]: true }));
      
      toast({
        title: "Reprocessing Started",
        description: "Extracting contact information...",
      });

      // Convert data URL to blob
      const response = await fetch(frontImage);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append('frontImage', blob, 'card.jpg');
      
      const { scannedContactAPI } = await import('../services/api');
      await scannedContactAPI.processAndSave(formData);
      
      toast({
        title: "Reprocessing Complete",
        description: "Card has been reprocessed successfully.",
      });
      
      await loadCards();
    } catch (error) {
      console.error('Error reprocessing:', error);
      toast({
        title: "Reprocess Failed",
        description: error.response?.data?.detail || "Failed to reprocess card.",
        variant: "destructive"
      });
    } finally {
      setProcessing(prev => ({ ...prev, [cardId]: false }));
    }
  };

  const deleteCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    
    try {
      const { scannedContactAPI } = await import('../services/api');
      await scannedContactAPI.delete(cardId);
      
      toast({
        title: "Card Deleted",
        description: "Scanned card has been removed.",
      });
      
      await loadCards();
    } catch (error) {
      console.error('Error deleting:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete card.",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (card) => {
    if (card.isProcessing) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
          <Clock className="w-4 h-4 animate-spin" />
          Processing
        </span>
      );
    }
    
    if (card.error) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-sm font-semibold rounded-full">
          <AlertCircle className="w-4 h-4" />
          Failed
        </span>
      );
    }
    
    if (card.name && card.name !== 'Processing...' && card.name !== 'Unknown') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
          <CheckCircle className="w-4 h-4" />
          Completed
        </span>
      );
    }
    
    return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-semibold rounded-full">
          <AlertCircle className="w-4 h-4" />
          Needs Review
        </span>
      );
  };

  const stats = {
    total: cards.length,
    processing: cards.filter(c => c.isProcessing).length,
    completed: cards.filter(c => !c.isProcessing && !c.error && c.name && c.name !== 'Processing...' && c.name !== 'Unknown').length,
    failed: cards.filter(c => c.error).length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-8">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Admin: Scanned Cards Manager
          </h1>
          <p className="text-gray-600">
            Monitor and manually process business card scans
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card className="p-6 bg-white">
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Cards</div>
          </Card>
          
          <Card className="p-6 bg-blue-50 border-blue-200">
            <div className="text-3xl font-bold text-blue-700">{stats.processing}</div>
            <div className="text-sm text-blue-600">Processing</div>
          </Card>
          
          <Card className="p-6 bg-green-50 border-green-200">
            <div className="text-3xl font-bold text-green-700">{stats.completed}</div>
            <div className="text-sm text-green-600">Completed</div>
          </Card>
          
          <Card className="p-6 bg-red-50 border-red-200">
            <div className="text-3xl font-bold text-red-700">{stats.failed}</div>
            <div className="text-sm text-red-600">Failed</div>
          </Card>
        </div>

        {/* Actions */}
        <div className="mb-6 flex justify-end">
          <Button
            onClick={loadCards}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Cards List */}
        <div className="space-y-4">
          {cards.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              No scanned cards found
            </Card>
          ) : (
            cards.map(card => (
              <Card key={card._id} className="p-6">
                <div className="flex gap-6">
                  {/* Card Image */}
                  {card.frontImage && (
                    <div className="flex-shrink-0">
                      <img
                        src={card.frontImage}
                        alt="Card"
                        className="w-32 h-20 object-cover rounded border-2 border-gray-200"
                      />
                    </div>
                  )}

                  {/* Card Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{card.name || 'No Name'}</h3>
                        {card.company && <p className="text-gray-600">{card.company}</p>}
                        {card.title && <p className="text-sm text-gray-500">{card.title}</p>}
                      </div>
                      {getStatusBadge(card)}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      {card.email && (
                        <div className="text-gray-700">
                          <span className="font-semibold">Email:</span> {card.email}
                        </div>
                      )}
                      {card.phone && (
                        <div className="text-gray-700">
                          <span className="font-semibold">Phone:</span> {card.phone}
                        </div>
                      )}
                      {card.locationAddress && (
                        <div className="text-gray-700 col-span-2">
                          <span className="font-semibold">Address:</span> {card.locationAddress}
                        </div>
                      )}
                    </div>

                    {card.error && (
                      <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                        <p className="text-sm text-red-700 font-semibold">Error:</p>
                        <p className="text-sm text-red-600">{card.error}</p>
                      </div>
                    )}

                    <div className="text-xs text-gray-400">
                      Created: {new Date(card.createdAt).toLocaleString()}
                      {card.processingCompletedAt && (
                        <> • Completed: {new Date(card.processingCompletedAt).toLocaleString()}</>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reprocessCard(card._id, card.frontImage)}
                      disabled={processing[card._id] || !card.frontImage}
                      className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${processing[card._id] ? 'animate-spin' : ''}`} />
                      Reprocess
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteCard(card._id)}
                      className="border-2 border-red-500 text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminScannedCards;
